import React from 'react';
import styled from 'styled-components';
import { Card, Input, Dropdown, IGNIS_RED } from '@120wateraudit/waterworks';
import { Form } from '@120wateraudit/form-builder-components';
import { MonthlyOperatingReportCategory } from './MonthlyOperatingReportCategory';

export interface MetadataErrors {
  title?: string;
  description?: string;
  subCategory?: string;
}

interface MetadataStepProps {
  formMeta: Omit<Form, 'config'>;
  errors?: MetadataErrors;
  onChange: (formMeta: Omit<Form, 'config'>) => void;
}

const MOR_TYPE_OPTIONS = Object.entries(MonthlyOperatingReportCategory).map(
  ([key, value]) => ({ value: key, text: value })
);

const MetadataStep: React.FC<MetadataStepProps> = ({
  formMeta,
  errors,
  onChange
}) => (
  <Wrapper>
    <StyledCard>
      <Header>
        <Heading>Form Details</Heading>
        <Subheading>
          Give your form a name, description, and category so utilities can
          identify it.
        </Subheading>
      </Header>
      <Field>
        <Label htmlFor="form-title">Name</Label>
        <Input
          name="form-title"
          type="text"
          required
          value={formMeta.title}
          onChange={(e) => onChange({ ...formMeta, title: e.target.value })}
          placeholder="e.g. Monthly Surface Water MOR"
          aria-label="Name"
        />
        <HelperText>Shown to utilities when they open the form.</HelperText>
        {errors?.title && <ErrorText>{errors.title}</ErrorText>}
      </Field>
      <Field>
        <Label htmlFor="form-description">Description</Label>
        <Input
          name="form-description"
          type="text"
          required
          value={formMeta.description}
          onChange={(e) =>
            onChange({ ...formMeta, description: e.target.value })
          }
          placeholder="Short summary of what this form is for"
          aria-label="Description"
        />
        <HelperText>
          A brief explanation that helps utilities choose the right form.
        </HelperText>
        {errors?.description && <ErrorText>{errors.description}</ErrorText>}
      </Field>
      <Field>
        <Label htmlFor="form-mor-type">MOR Type</Label>
        <Dropdown
          name="form-mor-type"
          required
          value={formMeta.subCategory ?? ''}
          onChange={(e) =>
            onChange({ ...formMeta, subCategory: e.target.value })
          }
          options={MOR_TYPE_OPTIONS}
          placeholder="Select MOR type"
          aria-label="MOR Type"
        />
        <HelperText>
          The Monthly Operating Report category this form applies to.
        </HelperText>
        {errors?.subCategory && <ErrorText>{errors.subCategory}</ErrorText>}
      </Field>
    </StyledCard>
  </Wrapper>
);

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;
  border-radius: 12px;
  width: 100%;
  max-width: 640px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Heading = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
`;

const Subheading = styled.p`
  margin: 0;
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.9375rem;
  line-height: 1.4;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;

  input,
  select {
    width: 100%;
  }
`;

const Label = styled.label`
  font-weight: 600;
`;

const HelperText = styled.span`
  color: rgba(0, 0, 0, 0.55);
  font-size: 0.8125rem;
`;

const ErrorText = styled.span`
  color: ${IGNIS_RED};
  font-size: 0.875rem;
`;

export default MetadataStep;
